import { createSelector } from '@reduxjs/toolkit';
import { get as _get } from 'lodash';
import { DriverDefaults } from '../../Constants/DriverConstants';
import { GenericProductType } from '../../types/type';
import { mapPartnerCodeToServicingOpcoId } from '../../utils/Utilities';
import { RootState } from './store';

export const getUrlKey = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => state.urlKey
);

export const getSessionId = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => state.sessionId
);

export const getAuthKey = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => state.authKey
);

export const userIsAdjuster = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => _get(state, 'blobResponse.customerInfo.roleType', '').toLowerCase() === 'adjuster'
);

export const userIsThirdParty = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => _get(state, 'blobResponse.customerInfo.roleType', '').toLowerCase() === 'thirdparty'
);

export const getClaimNumber = createSelector(
  (state: RootState) => state as RootState,
  (state) =>
    _get(
      state,
      'claimDetail.claimSummary.claimNumber',
      _get(state, 'contextRouter.response.blobResponse.claimsInfo.fullClaimNumber', '')
    )
);

export const getZipCode = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => _get(state.blobResponse, 'claimsInfo.detailedPoliciesInfo[0].policyAddress[0].zipCode5')
);

export const getUserData = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => _get(state.blobResponse, 'customerInfo')
);

// Servicing opco: copy, phone numbers, question sets, eligible services, navigation, photo-upload theme id and source system
export const getServicingOpCo = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) =>
    mapPartnerCodeToServicingOpcoId(
      _get(state.blobResponse, 'distributionChannel.opco'),
      _get(state.blobResponse, 'distributionChannel.partnerId')
    )
);

export const getMobileSourceSystem = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => _get(state.blobResponse, 'distributionChannel.sourceSystem', '')
);

export const getPartnerName = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => _get(state.blobResponse, 'distributionChannel.partnerName', '').toLowerCase()
);

export const getPartnerId = createSelector(
  (state: RootState) => (state as RootState).contextRouter,
  (state) => _get(state.blobResponse, 'distributionChannel.partnerId', '').toLowerCase()
);

export const getClaimPlnarInfo = createSelector(
  (state: RootState) => (state as RootState).claimDetail,
  (state) => _get(state, 'claimPlnarInfo')
);

export const getClaimPhotoInfo = createSelector(
  (state: RootState) => (state as RootState).claimDetail,
  (state) => _get(state, 'claimPhotos')
);

export const getCvpRefNumber = createSelector(
  (state: RootState) => (state as RootState).claimDetail,
  (state) => _get(state, 'claimSummary.cvpRefNumber')
);

export const getCvpEligible = createSelector(
  (state: RootState) => (state as RootState).services,
  (state) => _get(state, 'eligibleCvpService')
);

export const getInsuredLastName = createSelector(
  (state: RootState) => (state as RootState).claimDetail,
  (state) => _get(state, 'claimSummary.insured.lastName')
);

export const getQuestionSetPartner = createSelector(
  (state: RootState) => (state as RootState).claimCreation,
  (state) => _get(state, 'riskInfo.partnerId', '')
);

export const getPolicyInsureds = createSelector(
  (state: RootState) => (state as RootState).policies,
  (state) => {
    const names: any[] = [];
    let i = 0;

    const risks: any[] = [];
    _get(state, 'policies', []).forEach((policy: any) => {
      _get(policy, 'risks', []).forEach((risk: any) => {
        risks.push(risk);
      });
    });

    risks.forEach((risk) => {
      _get(risk, 'policy.policyRoles', []).forEach((role: any) => {
        const newOperator = {
          id: i.toString(),
          contactReferenceKey: _get(role, 'contactReferenceKey'),
          firstName: _get(role, 'firstName', '').trim(),
          lastName: _get(role, 'lastName', '').trim(),
          isPrimary: _get(role, 'roleType', '').trim() === 'PrimaryNamedInsured',
          policyNumber: _get(risk, 'policyNumber', ''),
        };
        // Only add newOperator if we haven't already added it to the list
        if (
          newOperator.firstName.trim() &&
          !names.find(
            (name) =>
              name.firstName.toLowerCase() === newOperator.firstName.toLowerCase() &&
              name.lastName.toLowerCase() === newOperator.lastName.toLowerCase() &&
              name.policyNumber === newOperator.policyNumber
          )
        ) {
          names.push(newOperator);
          i++;
        }
      });

      // Loop through names listed on vehicle roles
      _get(risk, 'vehicleRoles', []).forEach((role: any) => {
        const newOperator = {
          id: i.toString(),
          contactReferenceKey: _get(role, 'contactReferenceKey'),
          firstName: _get(role, 'firstName', '').trim(),
          lastName: _get(role, 'lastName', '').trim(),
          isPrimary: false,
          policyNumber: _get(risk, 'policyNumber', ''),
        };
        // Only add newOperator if we haven't already added it to the list
        if (
          newOperator.firstName.trim() &&
          !names.find(
            (name) =>
              name.firstName.toLowerCase() === newOperator.firstName.toLowerCase() &&
              name.lastName.toLowerCase() === newOperator.lastName.toLowerCase() &&
              name.policyNumber === newOperator.policyNumber
          )
        ) {
          names.push(newOperator);
          i++;
        }
      });
    });

    return names;
  }
);

export const getDriverNames = createSelector(getPolicyInsureds, (insureds) => {
  const names: any[] = [];

  insureds.forEach((insured) => {
    names.push({
      id: insured.id,
      text: _get(insured, 'firstName', '').trim() + ' ' + _get(insured, 'lastName', '').trim(),
      isSelected: false,
      policyNumber: insured.policyNumber,
      mask: true,
    });
  });

  names.push({
    id: DriverDefaults.NO_DRIVER_IN_VEHICLE,
    text: DriverDefaults.NO_DRIVER_IN_VEHICLE,
  });

  // Driver not on policy
  names.push({
    id: DriverDefaults.DRIVER_NOT_ON_POLICY,
    text: DriverDefaults.DRIVER_NOT_ON_POLICY,
  });

  return names;
});

export const getClaimRisks = createSelector(
  (state: RootState) => (state as RootState).policies,
  (state) => {
    const risks: any[] = [];
    _get(state, 'policies', []).forEach((policy: any) => {
      _get(policy, 'risks', []).forEach((risk: any) => {
        // Only include personal auto and personal property risks
        // Exclude commercial policies and workers comp from risk selection
        if (
          risk.generalizedProductType === GenericProductType.WorkersComp ||
          risk.generalizedProductType === GenericProductType.CommercialAuto ||
          risk.generalizedProductType === GenericProductType.CommercialProperty
        ) {
          return;
        }

        if (risk.generalizedProductType === GenericProductType.Auto) {
          risks.push({
            id: risk.id,
            policyNumber: risk.policyNumber,
            periodStartDate: risk.periodStartDate,
            generalizedProductType: risk.generalizedProductType,
            iconType: risk.iconType,
            description: risk.description,
            vin: risk.vin,
            year: risk.year,
            make: risk.make,
            model: risk.model,
            vehicleTypeName: risk.vehicleTypeName,
            address: risk.address,
            risk: risk.risk,
            policy: policy,
            partnerId: mapPartnerCodeToServicingOpcoId(policy.policyOpCo, policy.partnerCode),
            policySource: policy.policySource,
          });
        } else {
          risks.push({
            id: risk.id,
            policyNumber: risk.policyNumber,
            periodStartDate: risk.periodStartDate,
            generalizedProductType: risk.generalizedProductType,
            iconType: risk.iconType,
            description: risk.description,
            address: risk.address,
            risk: risk.risk,
            policy: policy,
            partnerId: mapPartnerCodeToServicingOpcoId(policy.policyOpCo, policy.partnerCode),
            policySource: policy.policySource,
          });
        }
      });
    });
    return risks.sort((a: any, b: any) => {
      if (a.generalizedProductType === GenericProductType.Auto) {
        if (b.generalizedProductType !== GenericProductType.Auto) {
          return -1;
        } else {
          return 0;
        }
      } else if (b.generalizedProductType === GenericProductType.Auto) {
        return 1;
      } else {
        return 0;
      }
    });
  }
);

export const getClaimCreationProductType = createSelector(
  (state: RootState) => (state as RootState).claimCreation,
  (state) => _get(state, 'riskInfo.generalizedProductType')
);

export const getFNOLDateTime = createSelector(
  (state: RootState) => (state as RootState).claimCreation,
  (state) => _get(state, 'dateLocationInfo')
);

export const getSelectedRiskPolicyNumber = createSelector(
  (state: RootState) => (state as RootState).claimCreation,
  (state) => _get(state, 'riskInfo.policyNumber')
);

export const getThirdPartyPolicy = createSelector(
  (state: RootState) => (state as RootState).thirdPartyClaims,
  (state) => _get(state, 'policyNumber')
);

export const getThirdPartyTypeOfGuest = createSelector(
  (state: RootState) => (state as RootState).thirdPartyClaims,
  (state) => _get(state, 'typeOfGuest')
);

// Feature Flags
export const getPhotoUploadCCCFlag = createSelector(
  (state: RootState) => (state as RootState).optimizelyFlags.data,
  (state) => _get(state, 'autoPhotoUploadCCC', true)
);

export const getAutoDrpSearchFlag = createSelector(
  (state: RootState) => (state as RootState).optimizelyFlags.data,
  (state) => _get(state, 'autoDrpSearchSelection', true)
);
export const getAutoRentalShopSelection = createSelector(
  (state: RootState) => (state as RootState).optimizelyFlags.data,
  (state) => _get(state, 'autoRentalShopSearchSelection', true)
);
